import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatBRL, formatPct, formatDate, formatDateTime, formatNum, parseDecimal } from "@/lib/format";
import { statusBadge } from "@/pages/Lots";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, ArrowLeft, Trash2, FileText, FileSpreadsheet, Camera } from "lucide-react";

const changeTypeLabel = {
  INITIAL_PRICE: "Preço inicial", MARKDOWN: "Remarcação (baixa)", MARKUP: "Remarcação (alta)", CORRECTION: "Correção",
};
const eventLabel = {
  ENTRADA: "Entrada", SAIDA_VENDA: "Saída (venda)", ESTORNO_VENDA: "Estorno (cancelamento)",
};

function Metric({ label, value, tone }) {
  const t = { success: "text-emerald-600 dark:text-emerald-400", danger: "text-red-600 dark:text-red-400", accent: "text-accent" }[tone] || "";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="section-label">{label}</p>
      <p className={`font-num text-xl font-bold mt-1 ${t}`}>{value}</p>
    </div>
  );
}

function ItemPhotoCell({ item, onDone }) {
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => { setErr(false); }, [item.photo_updated_at]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/items/${item.id}/catalog-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto de catálogo (fundo branco) gerada!");
      setErr(false);
      onDone();
    } catch (err2) { toast.error(apiError(err2)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const url = !err ? `${BACKEND}/api/items/${item.id}/photo?v=${item.photo_updated_at || ""}` : null;
  return (
    <div className="flex items-center">
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`item-photo-input-${item.internal_code}`} />
      {busy ? (
        <div className="h-10 w-10 rounded border border-border flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : url ? (
        <img src={url} loading="lazy" onError={() => setErr(true)} alt="" title="Trocar foto" onClick={() => ref.current?.click()} className="h-10 w-10 rounded object-cover border border-border cursor-pointer" />
      ) : (
        <button type="button" onClick={() => ref.current?.click()} title="Gerar foto de catálogo (fundo branco)" className="h-10 w-10 rounded border border-dashed border-border text-muted-foreground hover:text-accent hover:border-accent flex items-center justify-center text-lg" data-testid={`item-photo-add-${item.internal_code}`}>+</button>
      )}
    </div>
  );
}

function AddItemDialog({ lotId, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ internal_code: "", description: "", category: "", brand: "", condition: "", color: "", size: "", qty_received: 1, initial_price: "" });
  const [prefix, setPrefix] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get(`/lots/${lotId}/code-prefix`).then((r) => {
      const p = r.data.prefix || "";
      setPrefix(p);
      setF((cur) => ({ ...cur, internal_code: cur.internal_code && cur.internal_code.startsWith(p) ? cur.internal_code : p }));
    }).catch(() => {});
  }, [open, lotId]);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoRef = React.useRef(null);
  const resetForm = () => {
    setF({ internal_code: "", description: "", category: "", brand: "", condition: "", color: "", size: "", qty_received: 1, initial_price: "" });
    setPhotoFile(null);
    setPhotoPreview((p) => { if (p) URL.revokeObjectURL(p); return null; });
  };
  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (photoRef.current) photoRef.current.value = "";
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(file); });
    const fd = new FormData();
    fd.append("file", file);
    setAnalyzing(true);
    try {
      const { data } = await api.post("/items/analyze-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((cur) => ({
        ...cur,
        description: data.description || cur.description,
        category: data.category || cur.category,
        brand: data.brand || cur.brand,
        condition: data.condition || cur.condition,
        color: data.color || cur.color,
        size: data.size || cur.size,
      }));
      toast.success("Campos preenchidos pela IA. A foto com fundo branco será salva ao concluir.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setAnalyzing(false); }
  };
  const submit = async () => {
    if (!f.internal_code.trim() || !f.description.trim()) { toast.error("Código e descrição são obrigatórios"); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post("/items", { ...f, lot_id: lotId, qty_received: parseInt(f.qty_received) || 1, initial_price: parseDecimal(f.initial_price) || 0 });
      if (photoFile && created?.id) {
        setSavingPhoto(true);
        try {
          const fd = new FormData();
          fd.append("file", photoFile);
          await api.post(`/items/${created.id}/catalog-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e2) {
          toast.error("Mercadoria salva, mas a foto falhou: " + apiError(e2));
        } finally { setSavingPhoto(false); }
      }
      toast.success("Mercadoria cadastrada");
      setOpen(false);
      resetForm();
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild><Button size="sm" data-testid="item-add-button"><Plus className="h-4 w-4 mr-1" /> Mercadoria</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nova mercadoria</DialogTitle></DialogHeader>
        <div className="mb-1 flex items-center gap-3">
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhotoSelected} data-testid="item-photo-input" />
          {photoPreview ? (
            <img src={photoPreview} alt="Prévia" className="h-16 w-16 rounded-lg object-cover border border-border" data-testid="item-photo-preview" />
          ) : null}
          <div className="flex flex-col gap-1">
            <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} disabled={analyzing || saving} data-testid="item-analyze-photo">
              {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}{analyzing ? "Lendo a foto..." : (photoPreview ? "Trocar foto" : "Tirar / enviar foto (IA)")}
            </Button>
            {photoPreview && !analyzing && <span className="text-[11px] text-muted-foreground">A foto será salva com fundo branco ao concluir.</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Código interno *</Label><Input value={f.internal_code} onChange={(e) => { const v = e.target.value; setF({ ...f, internal_code: prefix && !v.startsWith(prefix) ? prefix : v }); }} data-testid="item-code-input" />{prefix && <p className="text-[11px] text-muted-foreground">Prefixo fixo de rastreio: <span className="font-num font-semibold">{prefix}</span></p>}</div>
          <div className="space-y-2"><Label>Preço inicial</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.initial_price} onChange={(e) => setF({ ...f, initial_price: e.target.value })} data-testid="item-price-input" /></div>
          <div className="space-y-2 col-span-2"><Label>Descrição *</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="item-desc-input" /></div>
          <div className="space-y-2"><Label>Categoria</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          <div className="space-y-2"><Label>Marca</Label><Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} /></div>
          <div className="space-y-2"><Label>Condição</Label><Input value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} placeholder="Ex.: Ótimo estado" /></div>
          <div className="space-y-2"><Label>Cor</Label><Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="item-color-input" /></div>
          <div className="space-y-2"><Label>Tamanho</Label><Input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} data-testid="item-size-input" placeholder="Ex.: P, M, G, 38" /></div>
          <div className="space-y-2"><Label>Quantidade</Label><Input type="number" min="1" value={f.qty_received} onChange={(e) => setF({ ...f, qty_received: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid="item-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{savingPhoto ? "Gerando foto (fundo branco)..." : saving ? "Salvando..." : "Salvar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RepriceDialog({ item, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ new_price: "", change_type: "MARKDOWN", reason: "" });
  const submit = async () => {
    const np = parseDecimal(f.new_price);
    if (isNaN(np) || np < 0) { toast.error("Preço inválido"); return; }
    setSaving(true);
    try {
      await api.post(`/items/${item.id}/reprice`, { new_price: np, change_type: f.change_type, reason: f.reason || null });
      toast.success("Preço remarcado");
      setOpen(false);
      setF({ new_price: "", change_type: "MARKDOWN", reason: "" });
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF({ new_price: String(item.current_price).replace(".", ","), change_type: "MARKDOWN", reason: "" }); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Remarcar {item.internal_code}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Preço vigente: <span className="font-num font-semibold text-foreground">{formatBRL(item.current_price)}</span></p>
          <div className="space-y-2"><Label>Novo preço</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.new_price} onChange={(e) => setF({ ...f, new_price: e.target.value })} data-testid="reprice-price-input" /></div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={f.change_type} onValueChange={(v) => setF({ ...f, change_type: v })}>
              <SelectTrigger data-testid="reprice-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MARKDOWN">Remarcação para baixo</SelectItem>
                <SelectItem value="MARKUP">Remarcação para cima</SelectItem>
                <SelectItem value="CORRECTION">Correção</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Motivo</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid="reprice-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Remarcar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCostDialog({ lotId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ cost_type: "AQUISICAO", description: "", amount: "", kind: "COST" });
  const submit = async () => {
    const amt = parseDecimal(f.amount);
    if (isNaN(amt) || amt < 0) { toast.error("Valor inválido"); return; }
    try {
      await api.post(`/lots/${lotId}/costs`, { ...f, amount: amt });
      toast.success("Custo lançado");
      setOpen(false);
      setF({ cost_type: "AQUISICAO", description: "", amount: "", kind: "COST" });
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="cost-add-button"><Plus className="h-4 w-4 mr-1" /> Custo</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Lançar custo do lote</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={f.cost_type} onValueChange={(v) => setF({ ...f, cost_type: v })}>
              <SelectTrigger data-testid="cost-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AQUISICAO">Aquisição</SelectItem>
                <SelectItem value="FRETE">Frete de entrada</SelectItem>
                <SelectItem value="HIGIENIZACAO">Higienização</SelectItem>
                <SelectItem value="REPARO">Reparos</SelectItem>
                <SelectItem value="CUSTOMIZACAO">Customização</SelectItem>
                <SelectItem value="TRIBUTO">Tributos não recuperáveis</SelectItem>
                <SelectItem value="OUTRO">Outro custo direto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Natureza</Label>
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="cost-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="COST">Custo (soma ao CTL)</SelectItem>
                <SelectItem value="DISCOUNT">Desconto / abatimento (reduz o CTL)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Valor</Label><Input type="text" inputMode="decimal" placeholder="0,00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="cost-amount-input" /></div>
          <div className="space-y-2"><Label>Descrição</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="cost-save-button">Lançar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [items, setItems] = useState([]);
  const [costs, setCosts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [sales, setSales] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, it, c, p, s, m] = await Promise.all([
        api.get(`/lots/${id}`),
        api.get(`/items`, { params: { lot_id: id } }),
        api.get(`/lots/${id}/costs`),
        api.get(`/lots/${id}/price-events`),
        api.get(`/lots/${id}/sales`),
        api.get(`/lots/${id}/movements`),
      ]);
      setLot(l.data); setItems(it.data); setCosts(c.data); setPrices(p.data); setSales(s.data); setMovements(m.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.patch(`/lots/${id}`, { status }); toast.success("Status atualizado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delCost = async (costId) => {
    try { await api.delete(`/lots/${id}/costs/${costId}`); toast.success("Custo removido"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/lots/${id}/export.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lote_${lot.code}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportação ${fmt.toUpperCase()} gerada`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading || !lot) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const ind = lot.indicators;

  return (
    <div>
      <button onClick={() => navigate("/lotes")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Lotes</button>

      <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm mb-6" data-testid="lote-ficha-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold font-num tracking-tight">{lot.code}</h1>
              {statusBadge(lot.status)}
              {lot.is_initial_balance && <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">Saldo inicial</span>}
            </div>
            <p className="text-muted-foreground mt-1">
              {lot.supplier_name || "Sem fornecedor"} · Entrada {formatDate(lot.entry_date)} · {ind.age_days ?? "—"} dias em estoque
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="lote-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="lote-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Select value={lot.status} onValueChange={setStatus}>
            <SelectTrigger className="w-[170px]" data-testid="lote-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ABERTO">Aberto</SelectItem>
              <SelectItem value="EM_QUEIMA">Em queima</SelectItem>
              <SelectItem value="LIQUIDADO">Liquidado</SelectItem>
            </SelectContent>
          </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Metric label="Custo Total (CTL)" value={formatBRL(ind.ctl)} />
          <Metric label="RPI" value={formatBRL(ind.rpi)} />
          <Metric label="Receita Realizada (RR)" value={formatBRL(ind.rr)} tone="success" />
          <Metric label="RPR" value={formatBRL(ind.rpr)} />
          <Metric label="RPA (RR+RPR)" value={formatBRL(ind.rpa)} tone="accent" />
          <Metric label="Margem Projetada" value={formatPct(ind.mgp_pct)} tone={ind.mgp_pct >= 0 ? "success" : "danger"} />
          <Metric label="Sell-through" value={formatPct(ind.sell_through)} />
          <Metric label="Recuperação Invest." value={formatPct(ind.recovery_pct)} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Metric label="Margem R$" value={formatBRL(ind.mgp_rs)} tone={ind.mgp_rs >= 0 ? "success" : "danger"} />
          <Metric label="Markdown Acum. R$" value={formatBRL(ind.markdown_rs)} tone="danger" />
          <Metric label="Qtd Disponível" value={formatNum(ind.qty_available)} />
          <Metric label="Qtd Vendida" value={formatNum(ind.qty_sold)} />
        </div>
      </div>

      <Tabs defaultValue="mercadorias">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="mercadorias" data-testid="lote-tab-mercadorias">Mercadorias</TabsTrigger>
          <TabsTrigger value="custos" data-testid="lote-tab-custos">Custos</TabsTrigger>
          <TabsTrigger value="historico-precos" data-testid="lote-tab-historico-precos">Histórico de preços</TabsTrigger>
          <TabsTrigger value="vendas" data-testid="lote-tab-vendas">Vendas</TabsTrigger>
          <TabsTrigger value="movimentos" data-testid="lote-tab-movimentos">Movimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="mercadorias">
          <div className="flex justify-end mb-3"><AddItemDialog lotId={id} onDone={load} /></div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Foto</TableHead><TableHead>Código</TableHead><TableHead>Descrição</TableHead><TableHead>Categoria</TableHead>
                <TableHead className="text-right">Preço inicial</TableHead><TableHead className="text-right">Preço vigente</TableHead>
                <TableHead className="text-right">Disp.</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Nenhuma mercadoria neste lote</TableCell></TableRow>}
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell><ItemPhotoCell item={it} onDone={load} /></TableCell>
                    <TableCell className="font-num font-semibold">{it.internal_code}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{it.description}</TableCell>
                    <TableCell>{it.category || "—"}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(it.initial_price)}</TableCell>
                    <TableCell className="text-right font-num font-semibold">{formatBRL(it.current_price)}</TableCell>
                    <TableCell className="text-right font-num">{it.qty_available}/{it.qty_received}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded font-medium ${it.status === "VENDIDO" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{it.status === "VENDIDO" ? "Vendido" : "Disponível"}</span></TableCell>
                    <TableCell>
                      {it.qty_available > 0 && <RepriceDialog item={it} onDone={load} trigger={<Button size="sm" variant="outline" data-testid={`mercadoria-remarcacao-button-${it.internal_code}`}>Remarcar</Button>} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="custos">
          <div className="flex justify-end mb-3"><AddCostDialog lotId={id} onDone={load} /></div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Descrição</TableHead><TableHead>Natureza</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Valor</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {costs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nenhum custo lançado</TableCell></TableRow>}
                {costs.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.cost_type}</TableCell>
                    <TableCell>{c.description || "—"}</TableCell>
                    <TableCell>{c.kind === "DISCOUNT" ? <span className="text-amber-600">Desconto</span> : "Custo"}</TableCell>
                    <TableCell>{formatDate(c.date)}</TableCell>
                    <TableCell className={`text-right font-num ${c.kind === "DISCOUNT" ? "text-amber-600" : ""}`}>{c.kind === "DISCOUNT" ? "-" : ""}{formatBRL(c.amount)}</TableCell>
                    <TableCell><Button size="icon" variant="ghost" onClick={() => delCost(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="historico-precos">
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data/hora</TableHead><TableHead>Tipo</TableHead><TableHead className="text-right">De</TableHead><TableHead className="text-right">Para</TableHead><TableHead className="text-right">Diferença</TableHead><TableHead>Motivo</TableHead></TableRow></TableHeader>
              <TableBody>
                {prices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Sem eventos de preço</TableCell></TableRow>}
                {prices.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{formatDateTime(p.timestamp)}</TableCell>
                    <TableCell>{changeTypeLabel[p.change_type] || p.change_type}</TableCell>
                    <TableCell className="text-right font-num">{p.previous_price == null ? "—" : formatBRL(p.previous_price)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(p.new_price)}</TableCell>
                    <TableCell className={`text-right font-num ${p.difference >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatBRL(p.difference)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{p.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="vendas">
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data/hora</TableHead><TableHead>Mercadoria</TableHead><TableHead className="text-right">Qtd</TableHead><TableHead className="text-right">Preço vigente</TableHead><TableHead className="text-right">Vendido</TableHead><TableHead className="text-right">Receita líq.</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {sales.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Sem vendas deste lote</TableCell></TableRow>}
                {sales.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{formatDateTime(s.timestamp)}</TableCell>
                    <TableCell className="font-num">{s.item_code}</TableCell>
                    <TableCell className="text-right font-num">{s.quantity}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(s.current_price_at_sale)}</TableCell>
                    <TableCell className="text-right font-num">{formatBRL(s.sold_price)}</TableCell>
                    <TableCell className="text-right font-num font-semibold">{formatBRL(s.net_revenue)}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded ${s.status === "CANCELADA" ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{s.status === "CANCELADA" ? "Cancelada" : "Confirmada"}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="movimentos">
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data/hora</TableHead><TableHead>Evento</TableHead><TableHead className="text-right">Qtd</TableHead><TableHead className="text-right">Saldo após</TableHead><TableHead>Observação</TableHead></TableRow></TableHeader>
              <TableBody>
                {movements.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Sem movimentos</TableCell></TableRow>}
                {movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{formatDateTime(m.timestamp)}</TableCell>
                    <TableCell>{eventLabel[m.event_type] || m.event_type}</TableCell>
                    <TableCell className={`text-right font-num ${m.quantity_change >= 0 ? "text-emerald-600" : "text-red-600"}`}>{m.quantity_change > 0 ? "+" : ""}{m.quantity_change}</TableCell>
                    <TableCell className="text-right font-num">{m.quantity_after}</TableCell>
                    <TableCell>{m.note || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
